import React from 'react';
import { companyInfo } from '../data/mock';
import { Button } from './ui/button';
import { ArrowRight, Calculator, CheckCircle } from 'lucide-react';

// High-quality solar panel hero image
const HERO_IMAGE = 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920&q=80';

const HeroSection = () => {
  const scrollToContact = () => {
    const kontaktElement = document.querySelector('#kontakt');
    if (kontaktElement) {
      kontaktElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToCalculator = () => {
    const calculatorElement = document.querySelector('#autarkie-rechner');
    if (calculatorElement) {
      calculatorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex flex-col lg:flex-row">
      {/* Left Content Area */}
      <div className="relative z-30 w-full lg:w-[45%] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center">
        {/* Subtle Pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        
        <div className="relative z-20 px-6 sm:px-10 lg:px-14 xl:px-20 py-28 lg:py-36">
          <div className="max-w-xl">
            {/* Expert Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-sun-orange/20 rounded-full mb-8 border border-sun-orange/30">
              <span className="text-sun-orange font-semibold text-sm tracking-wide">20+ Jahre Photovoltaik-Expertise</span>
            </div>
            
            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] xl:text-[3.5rem] font-bold text-white leading-[1.1] mb-6 tracking-tight">
              Produzieren Sie Ihren eigenen Strom
              <span className="block text-sun-orange mt-3">– unabhängig von steigenden Energiepreisen.</span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-lg lg:text-xl text-white/80 mb-10 leading-relaxed">
              Sunprojects plant und realisiert hochwertige Photovoltaiksysteme mit Speicher, 
              Wallbox und intelligenter Energie­steuerung.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button
                onClick={scrollToContact}
                data-testid="hero-cta-beratung"
                size="lg"
                className="bg-sun-orange hover:bg-sun-orange-dark text-white px-8 py-7 text-lg font-semibold group shadow-xl shadow-sun-orange/30 transition-all hover:shadow-sun-orange/40 hover:scale-[1.02]"
              >
                Kostenlose Beratung
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={scrollToCalculator}
                data-testid="hero-cta-rechner"
                variant="outline"
                size="lg"
                className="border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 px-8 py-7 text-lg font-semibold transition-all"
              >
                <Calculator className="mr-2 w-5 h-5" />
                Wirtschaftlichkeit berechnen
              </Button>
            </div>

            {/* Trust Points */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-white/70 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-sun-orange flex-shrink-0" />
                <span>20+ Jahre Erfahrung</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-sun-orange flex-shrink-0" />
                <span>Über 10 MW installiert</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-sun-orange flex-shrink-0" />
                <span>Regionale Betreuung</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Image Area */}
      <div className="relative w-full lg:w-[55%] min-h-[450px] lg:min-h-screen overflow-hidden bg-slate-900">
        {/* High-quality background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${HERO_IMAGE})`,
            backgroundPosition: 'center center',
            backgroundSize: 'cover',
            zIndex: 1
          }}
        />
        
        {/* Multi-layer gradient overlays for excellent text contrast */}
        {/* Left edge fade for seamless transition */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/60 to-transparent z-10 pointer-events-none" />
        
        {/* Bottom fade for mobile/tablet */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent lg:from-slate-900/30 z-10 pointer-events-none" />
        
        {/* Top right subtle fade */}
        <div className="absolute top-0 right-0 w-2/3 h-1/2 bg-gradient-to-bl from-slate-900/40 via-transparent to-transparent z-10 pointer-events-none" />
        
        {/* Bottom fade to next section (mobile) */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent lg:hidden z-10 pointer-events-none" />
      </div>
    </section>
  );
};

export default HeroSection;
